
import { defineComponent, nextTick } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import FillMyCalendarDialog from "@/components/social/FillMyCalendarDialog.vue";
import {
  getContentSeries,
  getContentSettings,
  listContentCampaigns,
  updateContentCampaign
} from "@/api/ghlContent.api.js";
import { isImageMediaUrl, isVideoMediaUrl } from "@/utils/socialPostPolicy.js";
import { publishSeriesDraftCampaign } from "@/utils/seriesDraftPublish";

function publishDraftErrorMessage(error: unknown) {
  const candidate = error as {
    message?: unknown;
    response?: { data?: { message?: unknown } };
  };
  if (candidate.message) {
    return String(candidate.message);
  }
  if (candidate.response && candidate.response.data && candidate.response.data.message) {
    return String(candidate.response.data.message);
  }
  return "Unable to publish this draft.";
}

type Filter = "all" | "posted" | "scheduled";

type CalendarInsights = {
  likes: number | null;
  shares: number | null;
  comments: number | null;
};

type CalendarPost = {
  id: number;
  day: number;
  calendarAt: Date;
  caption: string;
  captionSnippet: string;
  platformsLabel: string;
  status: string;
  statusLabel: string;
  scheduledAt: string | null;
  isScheduledFuture: boolean;
  thumbnailUrl: string;
  videoUrl: string;
  timeLabel: string;
  accountsLabel: string;
  insights: CalendarInsights | null;
  insightsLabel: string;
  isSeriesDraft: boolean;
  seriesId: number | null;
  autoPublishEligible: boolean;
  seriesDraftModeLabel: string;
  rawCampaign: any;
};

type CalendarCell = {
  day: number | null;
  posts: CalendarPost[];
  insightsLabel: string;
  insightsCompact: string;
};

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function monthRange(year: number, monthIndex: number) {
  const from = new Date(year, monthIndex, 1, 0, 0, 0, 0);
  const to = new Date(year, monthIndex + 1, 1, 0, 0, 0, 0);
  return { from, to };
}

function firstMediaUrl(content: any): string {
  const urls = Array.isArray(content?.mediaUrls) ? content.mediaUrls : [];
  return String(urls.find(Boolean) || "").trim();
}

function captionFromContent(content: any): string {
  return String(content?.caption || "").trim();
}

function isScheduledFuture(scheduledAt: string | null | undefined): boolean {
  if (!scheduledAt) return false;
  const when = new Date(scheduledAt);
  return !Number.isNaN(when.getTime()) && when.getTime() > Date.now();
}

function matchesFilter(post: CalendarPost, filter: Filter): boolean {
  if (filter === "all") return true;
  if (filter === "scheduled") return post.isScheduledFuture;
  return !post.isScheduledFuture;
}

function statusDisplay(status: string, isFuture: boolean): string {
  if (isFuture) return "scheduled";
  return status || "unknown";
}

function toInsightCount(value: unknown): number | null {
  if (value == null || value === "") {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function insightsFromStats(stats: any): CalendarInsights | null {
  const raw = stats?.insights;
  if (!raw) {
    return null;
  }
  const likes = toInsightCount(raw.likes);
  const shares = toInsightCount(raw.shares);
  const comments = toInsightCount(raw.comments);
  if (likes == null && shares == null && comments == null) {
    return null;
  }
  return { likes, shares, comments };
}

function formatEngagementInsights(insights: CalendarInsights | null): string {
  if (!insights) {
    return "—";
  }
  const parts: string[] = [];
  if (insights.likes != null) {
    parts.push(`${insights.likes} likes`);
  }
  if (insights.shares != null) {
    parts.push(`${insights.shares} shares`);
  }
  if (insights.comments != null) {
    parts.push(`${insights.comments} comments`);
  }
  return parts.length ? parts.join(", ") : "—";
}

function compactEngagementInsights(insights: CalendarInsights | null): string {
  if (!insights) {
    return "";
  }
  const parts: string[] = [];
  if (insights.likes != null) {
    parts.push(`${insights.likes} likes`);
  }
  if (insights.shares != null) {
    parts.push(`${insights.shares} shares`);
  }
  if (insights.comments != null) {
    parts.push(`${insights.comments} comments`);
  }
  return parts.join(" · ");
}

function sumInsightTotals(posts: CalendarPost[]): CalendarInsights | null {
  let likesSum = 0;
  let sharesSum = 0;
  let commentsSum = 0;
  let hasLikes = false;
  let hasShares = false;
  let hasComments = false;

  for (const post of posts) {
    if (post.isScheduledFuture || !post.insights) {
      continue;
    }
    if (post.insights.likes != null) {
      likesSum += post.insights.likes;
      hasLikes = true;
    }
    if (post.insights.shares != null) {
      sharesSum += post.insights.shares;
      hasShares = true;
    }
    if (post.insights.comments != null) {
      commentsSum += post.insights.comments;
      hasComments = true;
    }
  }

  if (!hasLikes && !hasShares && !hasComments) {
    return null;
  }

  return {
    likes: hasLikes ? likesSum : null,
    shares: hasShares ? sharesSum : null,
    comments: hasComments ? commentsSum : null
  };
}

function accountsLabel(stats: any): string {
  const posted = stats?.posted || 0;
  const failed = stats?.failed || 0;
  const skipped = stats?.skipped || 0;
  if (failed > 0 && posted > 0) {
    return `${posted} posted, ${failed} failed`;
  }
  if (failed > 0) {
    return `${failed} failed`;
  }
  if (skipped > 0 && posted > 0) {
    return `${posted} posted, ${skipped} skipped`;
  }
  if (posted > 0) {
    return `${posted} posted`;
  }
  return "—";
}

export default defineComponent({
  name: "content-calendar",
  components: { FillMyCalendarDialog },
  emits: ["edit-campaign"],
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const now = new Date();
    return {
      loading: true,
      loadError: "",
      filter: "all" as Filter,
      viewYear: now.getFullYear(),
      viewMonth: now.getMonth(),
      selectedDay: now.getDate(),
      campaigns: [] as any[],
      videoDialogVisible: false,
      videoDialogUrl: "",
      videoDialogCaption: "",
      videoDialogSession: 0,
      fillMyCalendarOpen: false,
      activeSeries: null as any,
      seriesPollTimer: null as ReturnType<typeof setInterval> | null,
      draftVoiceovers: {} as Record<number, string>,
      locationId: "",
      publishingDraftId: 0,
      publishingAll: false
    };
  },
  computed: {
    monthLabel(): string {
      return new Date(this.viewYear, this.viewMonth, 1).toLocaleString(undefined, {
        month: "long",
        year: "numeric"
      });
    },
    isViewingCurrentMonth(): boolean {
      const now = new Date();
      return this.viewYear === now.getFullYear() && this.viewMonth === now.getMonth();
    },
    todayDay(): number {
      return new Date().getDate();
    },
    weekdayLabels(): string[] {
      return WEEKDAY_LABELS;
    },
    calendarMonthKey(): string {
      return `${this.viewYear}-${this.viewMonth}`;
    },
    calendarPosts(): CalendarPost[] {
      const posts: CalendarPost[] = [];
      for (const campaign of this.campaigns) {
        const atRaw = campaign.calendarAt || campaign.scheduledAt || campaign.updatedAt || campaign.createdAt;
        const calendarAt = new Date(atRaw);
        if (Number.isNaN(calendarAt.getTime())) continue;
        if (calendarAt.getFullYear() !== this.viewYear || calendarAt.getMonth() !== this.viewMonth) {
          continue;
        }
        const content = campaign.content || {};
        const mediaUrl = firstMediaUrl(content);
        const caption = captionFromContent(content);
        const isFuture = isScheduledFuture(campaign.scheduledAt);
        const isSeriesDraft = campaign.status === "draft" && Boolean(campaign.seriesId);
        const insights = isFuture || isSeriesDraft ? null : insightsFromStats(campaign.stats);
        const autoPublishEligible = Boolean(campaign.autoPublishEligible);
        const draftLabel = isSeriesDraft ? "draft" : statusDisplay(campaign.status, isFuture);
        const seriesDraftModeLabel = isSeriesDraft
          ? (autoPublishEligible ? "Auto-publish" : "Review only")
          : "";
        posts.push({
          id: campaign.id,
          day: calendarAt.getDate(),
          calendarAt,
          caption,
          captionSnippet: caption.length > 48 ? `${caption.slice(0, 48)}…` : caption || `Post #${campaign.id}`,
          platformsLabel: (campaign.platforms || []).join(", ") || "—",
          status: campaign.status,
          statusLabel: draftLabel,
          scheduledAt: campaign.scheduledAt || null,
          isScheduledFuture: isFuture,
          thumbnailUrl: mediaUrl && isImageMediaUrl(mediaUrl) ? mediaUrl : "",
          videoUrl: mediaUrl && isVideoMediaUrl(mediaUrl) ? mediaUrl : "",
          timeLabel: calendarAt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
          accountsLabel: isSeriesDraft
            ? "Draft"
            : isFuture
              ? "Scheduled"
              : accountsLabel(campaign.stats),
          insights,
          insightsLabel: formatEngagementInsights(insights),
          isSeriesDraft,
          seriesId: campaign.seriesId ? Number(campaign.seriesId) : null,
          autoPublishEligible,
          seriesDraftModeLabel,
          rawCampaign: campaign
        });
      }
      return posts.sort((a, b) => a.calendarAt.getTime() - b.calendarAt.getTime());
    },
    filteredPosts(): CalendarPost[] {
      return this.calendarPosts.filter((post) => matchesFilter(post, this.filter));
    },
    calendarCells(): CalendarCell[] {
      const firstWeekday = new Date(this.viewYear, this.viewMonth, 1).getDay();
      const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
      const byDay = new Map<number, CalendarPost[]>();
      for (const post of this.filteredPosts) {
        const list = byDay.get(post.day) || [];
        list.push(post);
        byDay.set(post.day, list);
      }

      const buildCell = (day: number | null, posts: CalendarPost[]): CalendarCell => {
        const dayTotals = day == null ? null : sumInsightTotals(posts);
        return {
          day,
          posts,
          insightsLabel: formatEngagementInsights(dayTotals),
          insightsCompact: compactEngagementInsights(dayTotals)
        };
      };

      const cells: CalendarCell[] = [];
      for (let i = 0; i < firstWeekday; i += 1) {
        cells.push(buildCell(null, []));
      }
      for (let day = 1; day <= daysInMonth; day += 1) {
        cells.push(buildCell(day, byDay.get(day) || []));
      }
      while (cells.length % 7 !== 0) {
        cells.push(buildCell(null, []));
      }
      return cells;
    },
    selectedDayPosts(): CalendarPost[] {
      return this.filteredPosts.filter((post) => post.day === this.selectedDay);
    },
    selectedDayHeading(): string {
      const date = new Date(this.viewYear, this.viewMonth, this.selectedDay);
      return date.toLocaleDateString(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric"
      });
    },
    postedThisMonth(): number {
      return this.calendarPosts.filter((post) => !post.isScheduledFuture).length;
    },
    scheduledThisMonth(): number {
      return this.calendarPosts.filter((post) => post.isScheduledFuture).length;
    },
    needsAttentionCount(): number {
      return this.calendarPosts.filter((post) => (
        post.status === "partial" || post.status === "failed"
      )).length;
    },
    monthEngagementLabel(): string {
      return formatEngagementInsights(sumInsightTotals(this.filteredPosts));
    },
    selectedDayEngagementLabel(): string {
      return formatEngagementInsights(sumInsightTotals(this.selectedDayPosts));
    },
    seriesDraftPosts(): CalendarPost[] {
      return this.calendarPosts.filter((post) => post.isSeriesDraft);
    },
    seriesDraftCount(): number {
      return this.seriesDraftPosts.length;
    },
    seriesStatusMessage(): string {
      if (this.activeSeries?.status === "generating") {
        return "Generating drafts…";
      }
      if (this.seriesDraftCount > 0) {
        return `${this.seriesDraftCount} draft${this.seriesDraftCount === 1 ? "" : "s"} ready to review.`;
      }
      return "";
    },
    selectedDayHasSeriesDrafts(): boolean {
      return this.selectedDayPosts.some((post) => post.isSeriesDraft);
    }
  },
  watch: {
    filter() {
      this.ensureSelectedDayHasPosts();
    },
    calendarMonthKey() {
      if (!this.loading) {
        this.loadMonth();
      }
    }
  },
  async mounted() {
    await Promise.all([this.loadMonth(), this.loadLocationId()]);
    this.loading = false;
  },
  beforeUnmount() {
    this.stopSeriesPolling();
  },
  methods: {
    async loadLocationId() {
      try {
        const response = await getContentSettings();
        this.locationId = String(response.data?.locationId || "").trim();
      } catch {
        this.locationId = "";
      }
    },
    syncDraftVoiceovers() {
      const next: Record<number, string> = { ...this.draftVoiceovers };
      for (const post of this.seriesDraftPosts) {
        const content = post.rawCampaign?.content || {};
        const script = content.reelScript || {};
        const voiceover = String(script.voiceover || content.caption || "").trim();
        if (!next[post.id] && voiceover) {
          next[post.id] = voiceover;
        }
      }
      this.draftVoiceovers = next;
    },
    stopSeriesPolling() {
      if (this.seriesPollTimer) {
        clearInterval(this.seriesPollTimer);
        this.seriesPollTimer = null;
      }
    },
    onFillSeriesStarted(series: any) {
      this.activeSeries = series;
      this.startSeriesPolling(Number(series?.id || 0));
      this.loadMonth();
    },
    startSeriesPolling(seriesId: number) {
      this.stopSeriesPolling();
      if (!seriesId) return;
      const poll = async () => {
        try {
          const response = await getContentSeries(seriesId);
          this.activeSeries = response.data?.series || null;
          await this.loadMonth();
          this.syncDraftVoiceovers();
          if (this.activeSeries?.status !== "generating") {
            this.stopSeriesPolling();
          }
        } catch {
          // keep polling briefly
        }
      };
      poll();
      this.seriesPollTimer = setInterval(poll, 3000);
    },
    buildCampaignForPublish(post: CalendarPost) {
      const campaign = post.rawCampaign || {};
      const content = { ...(campaign.content || {}) };
      const voiceover = String(this.draftVoiceovers[post.id] || "").trim();
      const reelScript = { ...(content.reelScript || {}), voiceover };
      return {
        ...campaign,
        content: {
          ...content,
          caption: String(content.caption || voiceover).trim(),
          reelScript: {
            ...reelScript,
            caption: String(content.caption || voiceover).trim()
          }
        }
      };
    },
    async editSeriesDraft(post: CalendarPost) {
      if (!post.isSeriesDraft) return;
      await this.saveSeriesDraftVoiceover(post);
      this.$emit("edit-campaign", post.id);
    },
    async saveSeriesDraftVoiceover(post: CalendarPost) {
      if (!post.isSeriesDraft) return;
      const voiceover = String(this.draftVoiceovers[post.id] || "").trim();
      if (!voiceover) return;
      const campaign = post.rawCampaign || {};
      const content = campaign.content || {};
      try {
        await updateContentCampaign(post.id, {
          content: {
            ...content,
            caption: String(content.caption || voiceover).trim(),
            reelScript: {
              ...(content.reelScript || {}),
              voiceover,
              caption: String(content.caption || voiceover).trim()
            }
          },
          platforms: campaign.platforms || [],
          scheduledAt: campaign.scheduledAt || undefined,
          publishNow: false
        }, []);
        await this.loadMonth();
      } catch (e) {
        console.error("Failed to save draft voiceover:", e);
      }
    },
    async publishSeriesDraft(post: CalendarPost, options: { silent?: boolean } = {}) {
      if (!post.isSeriesDraft) return;
      this.publishingDraftId = post.id;
      try {
        await this.saveSeriesDraftVoiceover(post);
        const campaign = this.buildCampaignForPublish(post);
        await publishSeriesDraftCampaign({
          campaign,
          locationId: this.locationId
        });
        await this.loadMonth();
        if (!options.silent) {
          Swal.fire({
            title: "Scheduled",
            text: "Your post is scheduled.",
            icon: "success",
            confirmButtonClass: "btn btn-secondary"
          });
        }
      } catch (e) {
        Swal.fire({
          title: "",
          text: publishDraftErrorMessage(e),
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
        throw e;
      } finally {
        this.publishingDraftId = 0;
      }
    },
    async publishAllSeriesDrafts() {
      if (this.publishingAll || !this.seriesDraftPosts.length) return;
      this.publishingAll = true;
      const drafts = this.seriesDraftPosts.slice();
      try {
        for (const post of drafts) {
          await this.publishSeriesDraft(post, { silent: true });
        }
        Swal.fire({
          title: "Scheduled",
          text: "Your drafts are scheduled.",
          icon: "success",
          confirmButtonClass: "btn btn-secondary"
        });
      } catch {
        // error toast already shown for the failed draft
      } finally {
        this.publishingAll = false;
      }
    },
    async loadMonth() {
      this.loadError = "";
      const { from, to } = monthRange(this.viewYear, this.viewMonth);
      try {
        const response = await listContentCampaigns({
          from: from.toISOString(),
          to: to.toISOString()
        });
        this.campaigns = response.data.campaigns || [];
        this.syncDraftVoiceovers();
        this.ensureSelectedDayHasPosts();
      } catch (e) {
        console.error("Failed to load content calendar:", e);
        this.campaigns = [];
        this.loadError = "Unable to load the calendar. Please try again.";
      }
    },
    ensureSelectedDayHasPosts() {
      const daysWithPosts = new Set(this.filteredPosts.map((post) => post.day));
      if (daysWithPosts.has(this.selectedDay)) {
        return;
      }
      const first = this.filteredPosts[0];
      if (first) {
        this.selectedDay = first.day;
        return;
      }
      if (this.isViewingCurrentMonth) {
        this.selectedDay = this.todayDay;
      } else {
        this.selectedDay = 1;
      }
    },
    shiftMonth(delta: number) {
      const next = new Date(this.viewYear, this.viewMonth + delta, 1);
      this.viewYear = next.getFullYear();
      this.viewMonth = next.getMonth();
    },
    selectDay(cell: { day: number | null; posts: CalendarPost[] }) {
      if (!cell.day || !cell.posts.length) return;
      this.selectedDay = cell.day;
    },
    openVideoDialog(post: CalendarPost) {
      this.videoDialogSession += 1;
      const session = this.videoDialogSession;
      this.videoDialogUrl = post.videoUrl;
      this.videoDialogCaption = post.caption || `Post #${post.id}`;

      const showDialog = () => {
        if (session !== this.videoDialogSession) return;
        this.videoDialogVisible = true;
      };

      if (this.videoDialogVisible) {
        this.videoDialogVisible = false;
        nextTick(showDialog);
        return;
      }
      showDialog();
    },
    onVideoDialogOpened() {
      nextTick(() => {
        const dialogVideo = this.$refs.dialogVideo as HTMLVideoElement | undefined;
        if (!dialogVideo) return;
        dialogVideo.currentTime = 0;
        dialogVideo.play().catch(() => {
          // User can press play via native controls.
        });
      });
    },
    onVideoDialogClosed() {
      if (this.videoDialogVisible) {
        return;
      }
      this.videoDialogUrl = "";
      this.videoDialogCaption = "";
    }
  }
});
